import React, { Dispatch, SetStateAction } from 'react';
import { Button, Dialog, Group } from '@economic/taco';
import { t, tBB } from '../../../../../../../shared/components/l10n/l10n';
import { SmartPayOnboardingStatus, SMARTPAY_PLAN_OPTIONS } from '../../../types';
import PlanOption from '../../../../../../../shared/components/PlanOption';
import ExternalLink from '../../../../../../../shared/components/ExternalLink';
import date from '../../../../../../../shared/util/utilities/date';
import track from '../../../../../../../shared/util/utilities/mixpanel';

interface Props {
    currentPlan?: null | number; //determines which plan is active, if there is no plan, then it means user is onboarding
    status: number;
    lastSubscriptionDate?: Date;
    planState: [number, Dispatch<SetStateAction<number>>];
    next: () => void;
    back: () => void;
}

const SelectPlanStep = ({
    currentPlan,
    status,
    lastSubscriptionDate,
    next,
    back,
    planState: [selectedPlan, setSelectedPlan],
}: Props) => {
    const onPlanClicked = (planId: number) => {
        setSelectedPlan(planId);

        if (currentPlan) track('smartpay', `update.modal.selected-plan-${planId === 1 ? 'regular' : 'subscription'}`);
        else track('smartpay', `signup.modal.selected.plan-${planId === 1 ? 'regular' : 'subscription'}`);
    };

    const onNext = () => {
        if (currentPlan)
            track('smartpay', `update.modal.select.plan-${selectedPlan === 1 ? 'regular' : 'subscription'}.pressed.next`);
        else track('smartpay', `signup.modal.select.plan-${selectedPlan === 1 ? 'regular' : 'subscription'}.pressed.next`);

        next();
    };

    return (
        <>
            <Dialog.Title>
                <div className="my-4">
                    {currentPlan ? t`SMARTPAY_CHANGE_PLAN_MODAL_TITLE` : t`SMARTPAY_ONBOARDING_MODAL_SELECT_PLAN_TITLE`}
                </div>
            </Dialog.Title>

            <div className="flex flex-col items-center">
                <span className="text-center">
                    {currentPlan === 1
                        ? tBB('SMARTPAY_CHANGE_PLAN_MODAL_NO_SUBSCRIPTION_TEXT', {
                              __LINK: content => (
                                  <ExternalLink href="https://www.e-conomic.dk/abonnementsvilkaar-e-conomic">
                                      {content}
                                  </ExternalLink>
                              ),
                          })
                        : currentPlan === 2
                        ? tBB('SMARTPAY_CHANGE_PLAN_MODAL_WITH_SUBSCRIPTION_TEXT', {
                              __LINK: content => (
                                  <ExternalLink href="https://www.e-conomic.dk/abonnementsvilkaar-e-conomic">
                                      {content}
                                  </ExternalLink>
                              ),
                          })
                        : t`SMARTPAY_ONBOARDING_MODAL_SELECT_PLAN_TEXT`}
                </span>
                <div className="flex justify-around my-6">
                    {SMARTPAY_PLAN_OPTIONS.map((planDetails, i) => (
                        <PlanOption
                            key={i}
                            planDetails={{
                                ...planDetails,
                                name: tBB(planDetails.name),
                                price: tBB(planDetails.price),
                                features: (
                                    <>
                                        {tBB(planDetails.features)}
                                        {status < SmartPayOnboardingStatus.Completed && planDetails.id === 2 && (
                                            <div className="mt-4 italic text-xs">
                                                {tBB('SMARTPAY_ONBOARDING_MODAL_PLAN_OPTION_SUBSCRIPTION_DISCLAIMER')}
                                            </div>
                                        )}

                                        {/* if user have recently unsubscribed from SmartPay but plan is still active until the end of the quarter */}
                                        {planDetails.id === 2 && currentPlan == 1 && lastSubscriptionDate && (
                                            <div className="mt-4 italic text-xs">
                                                {tBB('SMARTPAY_CHANGE_PLAN_MODAL_RECENLTY_DOWNGRADED_PLAN_DISCLAIMER', {
                                                    DATE: date.format(lastSubscriptionDate),
                                                })}
                                            </div>
                                        )}
                                    </>
                                ),
                            }}
                            selected={selectedPlan === planDetails.id}
                            onSelect={() => onPlanClicked(planDetails.id)}
                        />
                    ))}
                </div>
            </div>

            <Dialog.Footer>
                <Group>
                    <Button onClick={back}>
                        {currentPlan ? t`SMARTPAY_CHANGE_PLAN_MODAL_CANCEL_BUTTON` : t`SMARTPAY_ONBOARDING_MODAL_BACK_BUTTON`}
                    </Button>
                    <Button
                        autoFocus
                        appearance="primary"
                        onClick={onNext}
                        disabled={selectedPlan === undefined || selectedPlan === null}>
                        {currentPlan ? t`SMARTPAY_CHANGE_PLAN_MODAL_CONTINUE_BUTTON` : t`SMARTPAY_ONBOARDING_MODAL_NEXT_BUTTON`}
                    </Button>
                </Group>
            </Dialog.Footer>
        </>
    );
};

export default SelectPlanStep;
